/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {DB_Object} from "@intuitionrobotics/ts-common";
import {adminRoles, AppRoleLevel, ID_ALL, OrgRoleLevel, TabPermissions} from "@app/app-shared/permission";
import {StorageKey_JWT} from "@intuitionrobotics/user-account/frontend";
import {OrganizationMap} from "../app/pages/ui/organizations/OrganizationsContext";

export const upsertImpl = <T extends DB_Object>(items: T[], item: T) => {
    const itemIdx = items.findIndex(_item => _item._id === item._id);
    const idx = itemIdx !== -1 ? itemIdx : items.length;
    items[idx] = item;
    return items;
};

export function getRelevantGroupContactIds(orgs: OrganizationMap) {
    const orgsPermissions = getUserOrgRoles();
    const relevantContactIdsForMessages = [];
    const allPerm = orgsPermissions[ID_ALL];
    for (const org of Object.values(orgs)) {
        const orgRoles = allPerm || orgsPermissions[org._id] || [];
        if (orgRoles.includes(OrgRoleLevel.CS_MESSAGE)) {
            relevantContactIdsForMessages.push(org.groupContactIds[0])
        }
        if (orgRoles.includes(OrgRoleLevel.CL_MESSAGE)) {
            relevantContactIdsForMessages.push(org.groupContactIds[1])
        }
        if (orgRoles.includes(OrgRoleLevel.EX_MESSAGE)) {
            relevantContactIdsForMessages.push(org.groupContactIds[2])
        }
    }
    return relevantContactIdsForMessages
}

export const dataURItoBlob = (dataURI: string) => {
// convert base64/URLEncoded data component to raw binary data held in a string
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0)
        byteString = atob(dataURI.split(',')[1]);
    else
        byteString = unescape(dataURI.split(',')[1]);
// separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
// write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], {type: mimeString});
}

// TODO move to more appropriate place
type JWTTokenInformation = {
    exp: number,
    iat: number,
    iss: string,
    session_id: string,
    roles: string[],
    contactId: string,
    orgRoles?: {
        [orgId: string]: OrgRoleLevel[]
    },
    appRoles?: AppRoleLevel[]
    tabPermissionsType?: TabPermissions
    account: {
        email: string,
        _id: string,
    },
    userId: string
}

export const decodeJWT = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload) as JWTTokenInformation;
}

export const getUserTokenInfo = () => {
    const jwt = StorageKey_JWT.get();
    return decodeJWT(jwt);
};

export const extractTabPermissions = (userInformation: JWTTokenInformation) => userInformation.tabPermissionsType;

export const getTabPermissions = () => {
    const userInformation = getUserTokenInfo();
    return extractTabPermissions(userInformation);
}

export const getUserId = () => {
    const userInformation: JWTTokenInformation = getUserTokenInfo();
    return userInformation.userId
}

export const hasUserRole = (roles: AppRoleLevel[]): boolean => {
    if (!roles.length)
        return true;
    const {appRoles} = getUserTokenInfo();
    return !!appRoles?.some(r => roles.includes(r));
}

export const isAdmin = (userInformation: JWTTokenInformation): boolean => {
    return !!userInformation.appRoles?.some(r => adminRoles.includes(r));
}
export const getUserOrgRoles = () => {
    const userInformation = getUserTokenInfo();
    return userInformation.orgRoles || {};
};

export const hasPermissionOrgRoles = (orgRoles: { [p: string]: OrgRoleLevel[] }, orgId: string, roles: OrgRoleLevel[]) => {
    if (!roles.length)
        return true;
    return (orgRoles[ID_ALL] || orgRoles[orgId])?.some(r => roles.includes(r)) || false;
};

export const hasUserOrgRoles = (orgId: string, roles: OrgRoleLevel[]): boolean => {
    const orgRoles = getUserOrgRoles();
    return hasPermissionOrgRoles(orgRoles, orgId, roles);
}

export const hasUserTabPermissions = (tabPermissions?: TabPermissions[]): boolean => {
    if (!tabPermissions)
        return true;
    const userTabPermissions = extractTabPermissions(getUserTokenInfo())
    return tabPermissions.some(t => t === userTabPermissions);
};